import { useEffect, useRef, useState, type ComponentProps } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDaysIcon,
  CheckIcon,
  FileTextIcon,
  ImageIcon,
  Maximize2Icon,
  ExternalLinkIcon,
  MailIcon,
  MapPinIcon,
  PackageIcon,
  PaperclipIcon,
  PhoneIcon,
  PlayIcon,
  RotateCcwIcon,
  StickyNoteIcon,
  TruckIcon,
  UserIcon,
} from 'lucide-react';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { cn } from '@/lib/utils';
import { CardBadge, CardBody, CardEyebrow, CardShell } from './card-primitives';
import { DraftActionCard } from './draft-cards';
import { HelpCard } from './help-card';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { NoteBody } from '@/components/NoteBody';
import { createEventNote, NOTE_CATEGORIES, type NoteCategory } from '@/lib/events';
import { eventStageTone } from '@/lib/event-status';
import { formatEventDate, formatEventTime } from '@/lib/event-time';
import type { PillTone } from '@bcecloud/portal-ui';
import { getProposal } from '@/proposals/lib/api';
import { formatMoney } from '@/proposals/lib/money';

/** Micro-pill colour classes per stage tone, matching the card's inline pills. */
const STAGE_TONE_CLASS: Readonly<Record<PillTone, string>> = {
  success: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-800',
  danger: 'bg-rose-50 text-rose-700',
  info: 'bg-violet-50 text-violet-700',
  brand: 'bg-brand/10 text-brand',
  neutral: 'bg-slate-100 text-slate-600',
};

/**
 * Rich rendering for the assistant's answers.
 *
 * The model references entities with a small link vocabulary — the relative
 * paths `/~/event/<id>`, `/~/shipment/<id>`, `/~/contact/<id>`, plus Google Maps
 * URLs — and this module turns those links into chips and cards. The `/~/`
 * sentinel is a relative path on purpose: markdown's URL sanitizer strips
 * unknown schemes (a `bce:` link would never reach us), but relative paths pass
 * through untouched. Cards fetch their own data by id, so what they show is
 * always authoritative — never whatever the model happened to type.
 */

/** A parsed assistant reference. */
export type AssistantRef =
  | { readonly kind: 'event'; readonly id: string }
  | { readonly kind: 'shipment'; readonly id: string }
  | { readonly kind: 'contact'; readonly id: string }
  | { readonly kind: 'program'; readonly id: string }
  | { readonly kind: 'proposal'; readonly id: string }
  | {
      readonly kind: 'staff';
      readonly id: string;
      readonly name: string;
      readonly title?: string;
      readonly phone?: string;
      readonly email?: string;
    }
  | {
      readonly kind: 'person';
      readonly name: string;
      readonly role?: string;
      readonly phone?: string;
      readonly email?: string;
    }
  | {
      readonly kind: 'draftNote';
      readonly eventId: string;
      readonly category: string;
      readonly subject: string;
      readonly body: string;
    }
  | { readonly kind: 'draftEmail'; readonly draftId: string }
  | {
      readonly kind: 'draftAction';
      readonly action: string;
      readonly params: Readonly<Record<string, string>>;
    }
  | { readonly kind: 'help'; readonly area: string; readonly slug: string }
  | { readonly kind: 'attachment'; readonly id: string }
  | { readonly kind: 'map'; readonly url: string }
  | { readonly kind: 'external'; readonly url: string }
  | { readonly kind: 'internal'; readonly to: string }
  | { readonly kind: 'plain'; readonly href: string };

const ENTITY_REF = /^\/~\/(event|shipment|contact|program|proposal)\/([a-f0-9]{24})$/i;

/**
 * Classifies a markdown link href into a rich-reference kind.
 *
 * Pure and total, so it is unit-tested directly and never throws on the
 * half-formed hrefs a stream can momentarily produce.
 *
 * @param href - The link href from the model's markdown.
 * @returns The reference kind and its payload.
 */
export function parseAssistantRef(href: string): AssistantRef {
  const entity = ENTITY_REF.exec(href.trim());
  if (entity?.[1] && entity[2]) {
    const kind = entity[1].toLowerCase() as 'event' | 'shipment' | 'contact' | 'program' | 'proposal';
    return { kind, id: entity[2] };
  }
  // A note the assistant drafted from chat. Saved only when the staff member
  // clicks Save on the preview card — never by the model itself.
  const draft = /^\/~\/draft-note\?(.*)$/i.exec(href.trim());
  if (draft) {
    const p = new URLSearchParams(draft[1] ?? '');
    const eventId = p.get('event') ?? '';
    if (/^[a-f0-9]{24}$/i.test(eventId)) {
      return {
        kind: 'draftNote',
        eventId,
        category: p.get('category') ?? 'Basic',
        subject: p.get('subject') ?? '',
        body: p.get('body') ?? '',
      };
    }
  }
  // A change the assistant proposes (a packing line, an assistant, a charity
  // plan...). The kind names the planner in draft-cards; the fields ride in
  // the query. Saved only when the person clicks Save on the card.
  const draftAction = /^\/~\/draft\/([a-z-]+)\?(.*)$/i.exec(href.trim());
  if (draftAction?.[1]) {
    return {
      kind: 'draftAction',
      action: draftAction[1].toLowerCase(),
      params: Object.fromEntries(new URLSearchParams(draftAction[2] ?? '')),
    };
  }
  // A help page, as a card with Open and (when the page has a tour) Show me.
  const help = /^\/~\/help\/([a-z0-9-]+)\/([a-z0-9-]+)$/i.exec(href.trim());
  if (help?.[1] && help[2]) {
    return { kind: 'help', area: help[1].toLowerCase(), slug: help[2].toLowerCase() };
  }
  // An email BESTie drafted. The draft lives server-side (owner-scoped); the
  // card fetches the rendered preview and sending is the human's click here.
  const attachment = /^\/~\/attachment\/([a-f0-9]{24})$/i.exec(href.trim());
  if (attachment?.[1]) {
    return { kind: 'attachment', id: attachment[1] };
  }
  const draftEmail = /^\/~\/draft-email\/([a-f0-9]{24})$/i.exec(href.trim());
  if (draftEmail?.[1]) {
    return { kind: 'draftEmail', draftId: draftEmail[1] };
  }
  // A staff member (portal user): id + details in the query string, so the card
  // shows full details with no fetch and links to their profile.
  const staff = /^\/~\/staff\/([^/?]+)(?:\?(.*))?$/i.exec(href.trim());
  if (staff) {
    const p = new URLSearchParams(staff[2] ?? '');
    const name = p.get('name') ?? '';
    const title = p.get('title') ?? '';
    const phone = p.get('phone') ?? '';
    const email = p.get('email') ?? '';
    return {
      kind: 'staff',
      id: staff[1] ?? '',
      name,
      ...(title ? { title } : {}),
      ...(phone ? { phone } : {}),
      ...(email ? { email } : {}),
    };
  }
  // An ad-hoc contact whose details come from the notes rather than a portal
  // user record — the fields ride in the query string, so no fetch is needed.
  const person = /^\/~\/person(?:\?(.*))?$/i.exec(href.trim());
  if (person) {
    const p = new URLSearchParams(person[1] ?? '');
    const name = p.get('name') ?? '';
    const role = p.get('role') ?? '';
    const phone = p.get('phone') ?? '';
    const email = p.get('email') ?? '';
    return {
      kind: 'person',
      name,
      ...(role ? { role } : {}),
      ...(phone ? { phone } : {}),
      ...(email ? { email } : {}),
    };
  }
  if (/^https?:\/\//i.test(href)) {
    if (/(^|\.)google\.[^/]+\/maps|maps\.google\.|google\.[^/]+\/maps/i.test(href)) {
      return { kind: 'map', url: href };
    }
    return { kind: 'external', url: href };
  }
  if (href.startsWith('/')) return { kind: 'internal', to: href };
  return { kind: 'plain', href };
}

/** Shipment card payload from `GET /shipments/:id`. */
interface ShipmentCardData {
  readonly id: string;
  readonly carrier: string | null;
  readonly trackingNumber: string | null;
  readonly trackingUrl: string | null;
  readonly status: string | null;
  readonly eta: string | null;
  readonly isReturn: boolean;
  readonly items: ReadonlyArray<{ readonly name: string; readonly quantity: number }>;
  readonly from: AddressData | null;
  readonly to: AddressData | null;
  readonly event: { readonly id: string; readonly label: string } | null;
}

/** Program card payload from `GET /programs/:id`. */
interface ProgramCardData {
  readonly name: string;
  readonly excerpt?: string;
  readonly length?: string;
  readonly groupSize?: string;
  readonly images?: string[];
  readonly video?: string | null;
}

/** Contact card payload from `GET /users/:id`. */
interface ContactCardData {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly role?: string;
}

interface AddressData {
  readonly name: string | null;
  readonly line: string | null;
  readonly city: string | null;
  readonly state: string | null;
}

/** The markdown `a` renderer: dispatches on the reference kind. */
function AssistantAnchor({ href, children }: ComponentProps<'a'>): React.ReactElement {
  const ref = parseAssistantRef(typeof href === 'string' ? href : '');
  switch (ref.kind) {
    case 'event':
      return <EventCard id={ref.id} label={children} />;
    case 'shipment':
      return <ShipmentCard id={ref.id} label={children} />;
    case 'contact':
      return <ContactCard id={ref.id} label={children} />;
    case 'program':
      return <ProgramCard id={ref.id} label={children} />;
    case 'proposal':
      return <ProposalCard id={ref.id} label={children} />;
    case 'staff':
      return <StaffCard id={ref.id} name={ref.name || String(children)} title={ref.title} phone={ref.phone} email={ref.email} />;
    case 'person':
      return <PersonCard name={ref.name || String(children)} role={ref.role} phone={ref.phone} email={ref.email} />;
    case 'draftNote':
      return (
        <DraftNoteCard
          eventId={ref.eventId}
          category={ref.category}
          subject={ref.subject}
          body={ref.body}
        />
      );
    case 'attachment':
      return <AttachmentCard id={ref.id} label={children} />;
    case 'draftEmail':
      return <DraftEmailCard draftId={ref.draftId} />;
    case 'draftAction':
      return <DraftActionCard kind={ref.action} params={ref.params} />;
    case 'help':
      return <HelpCard area={ref.area} slug={ref.slug} label={children} />;
    case 'map':
      return <MapCard url={ref.url}>{children}</MapCard>;
    case 'external':
      return (
        <ExternalAnchor href={ref.url}>
          {children} <ExternalLinkIcon aria-hidden className="inline size-3 -translate-y-px" />
        </ExternalAnchor>
      );
    case 'internal': {
      // The model sometimes links app paths directly; give those the same rich
      // treatment as the /~/ vocabulary so people/events/programs always card.
      const person = /^\/people\/([a-f0-9]{24})/.exec(ref.to);
      if (person?.[1]) return <ContactCard id={person[1]} label={children} />;
      const event = /^\/events\/([a-f0-9]{24})/.exec(ref.to);
      if (event?.[1]) return <EventCard id={event[1]} label={children} />;
      const program = /^\/programs\/([a-f0-9]{24})/.exec(ref.to);
      if (program?.[1]) return <ProgramCard id={program[1]} label={children} />;
      const proposal = /^\/proposals\/([a-f0-9]{24})/.exec(ref.to);
      if (proposal?.[1]) return <ProposalCard id={proposal[1]} label={children} />;
      const helpPage = /^\/help\/([a-z0-9-]+)\/([a-z0-9-]+)(?:\?|$)/i.exec(ref.to);
      if (helpPage?.[1] && helpPage[2]) return <HelpCard area={helpPage[1]} slug={helpPage[2]} label={children} />;
      return <EntityChip to={ref.to}>{children}</EntityChip>;
    }
    default:
      return <span>{children}</span>;
  }
}

/** Components passed to Streamdown so the assistant's links render richly. */
export const assistantMarkdownComponents = { a: AssistantAnchor };

/** An external link that opens safely in a new tab. */
function ExternalAnchor({ href, children }: { href: string; children: React.ReactNode }): React.ReactElement {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="font-medium text-brand underline-offset-2 hover:underline"
    >
      {children}
    </a>
  );
}

/** A compact inline chip that navigates within the admin app. */
function EntityChip({ to, children }: { to: string; children: React.ReactNode }): React.ReactElement {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 rounded-md bg-admin-subtle px-1.5 py-0.5 font-medium text-brand no-underline hover:bg-brand/10"
    >
      {children}
    </Link>
  );
}

/** A shimmering placeholder bar for a not-yet-loaded card detail line. */
function Skeleton({ className }: { className?: string }): React.ReactElement {
  return (
    <span className={cn('block animate-pulse rounded bg-admin-subtle', className ?? 'h-3 w-32')} />
  );
}

/**
 * A map card for a physical address.
 *
 * No fetch, so it renders instantly. Shows the address in a card and a clearly
 * labelled footer action that opens Google Maps in a new tab.
 *
 * @param props - The Google Maps URL and the address text to show.
 * @returns The map card.
 */
function MapCard({ url, children }: { url: string; children: React.ReactNode }): React.ReactElement {
  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <CardBadge>
            <MapPinIcon aria-hidden className="size-[18px]" />
          </CardBadge>
          <span className="min-w-0 flex-1">
            <CardEyebrow>Location</CardEyebrow>
            <span className="mt-0.5 block font-bold text-portal-ink">{children}</span>
          </span>
        </span>
      </CardBody>
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        className="flex items-center gap-2 border-t border-admin-line bg-admin-bg px-3.5 py-2.5 text-[11.5px] font-bold text-brand no-underline hover:bg-brand/5"
      >
        <MapPinIcon aria-hidden className="size-3.5 shrink-0" />
        Open in Google Maps
        <ExternalLinkIcon aria-hidden className="ml-auto size-3.5 shrink-0 text-admin-muted" />
      </a>
    </CardShell>
  );
}

/** Joins an address into a one-line string. */
function addressLine(address: AddressData): string {
  return [address.name, address.line, [address.city, address.state].filter(Boolean).join(', ')]
    .filter(Boolean)
    .join(' · ');
}

/** A rich shipment card: carrier, tracking, status, items and route, with actions. */
function ShipmentCard({ id, label }: { id: string; label: React.ReactNode }): React.ReactElement {
  const query = useQuery({
    queryKey: ['assistant-shipment', id],
    queryFn: ({ signal }) => getJson<ShipmentCardData>(`/shipments/${id}`, undefined, signal),
    staleTime: 60_000,
  });

  const s = query.data;
  const isReturn = s?.isReturn === true;
  // Show the model's label as the title immediately, then swap in the
  // authoritative carrier + tracking once the fetch resolves.
  const title = s ? [s.carrier, s.trackingNumber].filter(Boolean).join(' · ') || 'Shipment' : null;
  const eta = s?.eta ? new Date(s.eta).toLocaleDateString() : null;

  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <CardBadge muted={isReturn}>
            {isReturn ? (
              <RotateCcwIcon aria-hidden className="size-[18px]" />
            ) : (
              <TruckIcon aria-hidden className="size-[18px]" />
            )}
          </CardBadge>

          <span className="min-w-0 flex-1">
            <CardEyebrow>{isReturn ? 'Return authorization' : 'Shipment'}</CardEyebrow>
            <span className="mt-0.5 block truncate font-bold text-portal-ink">{title ?? label}</span>
            {query.isPending ? (
              <Skeleton className="mt-1.5 h-3 w-44" />
            ) : query.isError || !s ? (
              <span className="mt-1 block text-admin-micro text-admin-muted">
                Details unavailable
              </span>
            ) : isReturn ? (
              <span className="mt-1 block text-admin-micro text-admin-muted">
                Pre-authorized return label · billed only if used
              </span>
            ) : s.to || eta ? (
              <span className="mt-1 block text-admin-micro text-admin-muted">
                {s.to ? `To ${addressLine(s.to)}` : ''}
                {s.to && eta ? ' · ' : ''}
                {eta ? `ETA ${eta}` : ''}
              </span>
            ) : null}
          </span>

          {s?.status ? (
            <span
              className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold capitalize',
                isReturn ? 'bg-admin-subtle text-admin-muted' : 'bg-brand/10 text-brand',
              )}
            >
              {s.status.replace(/_/g, ' ').toLowerCase()}
            </span>
          ) : query.isPending ? (
            <Skeleton className="h-4 w-14 shrink-0 rounded-full" />
          ) : null}
        </span>

        {s && s.items.length > 0 ? (
          <span className="mt-2.5 block rounded-lg bg-admin-bg px-2.5 py-2 text-admin-micro text-portal-ink">
            {s.items.map((item, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <PackageIcon aria-hidden className="size-3 shrink-0 text-admin-muted" />
                {item.quantity}× {item.name}
              </span>
            ))}
          </span>
        ) : null}

        {(s?.trackingUrl || s?.event) && !isReturn ? (
          <span className="mt-2.5 flex flex-wrap items-center gap-2">
            {s?.trackingUrl ? (
              <a
                href={s.trackingUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-admin-micro font-bold text-white no-underline hover:brightness-110"
              >
                Track <ExternalLinkIcon aria-hidden className="size-3 opacity-70" />
              </a>
            ) : null}
            {s?.event ? (
              <Link
                to={`/events/${s.event.id}?tab=shipments`}
                className="inline-flex items-center rounded-md border border-admin-line px-2.5 py-1 text-admin-micro font-bold text-portal-ink no-underline hover:bg-admin-subtle"
              >
                Open event
              </Link>
            ) : null}
          </span>
        ) : null}
      </CardBody>
    </CardShell>
  );
}

/** Attachment metadata from `GET /attachments/:id`. */
interface AttachmentCardData {
  readonly _id: string;
  readonly name: string;
  readonly type: string | null;
  readonly description: string | null;
}

/**
 * A document card for an event attachment. Fetches its own metadata, shows a
 * type-appropriate icon, and opens the file — images in an in-panel lightbox,
 * everything else in a new tab. The file streams from the same authenticated
 * V3 view endpoint the event page uses (same-origin, session cookie).
 *
 * @param props - The attachment id and the model's link text.
 * @returns The attachment card.
 */
function AttachmentCard({ id, label }: { id: string; label: React.ReactNode }): React.ReactElement {
  const [lightbox, setLightbox] = useState(false);
  const query = useQuery({
    queryKey: ['assistant-attachment', id],
    queryFn: ({ signal }) => getJson<AttachmentCardData>(`/attachments/${id}`, undefined, signal),
    staleTime: 60_000,
  });

  const data = query.data;
  const viewUrl = `/api/v3/attachments/${id}/view`;
  const isImage = (data?.type ?? '').startsWith('image/');
  const title = data?.name ?? (typeof label === 'string' ? label : 'Attachment');

  function open(): void {
    // Images lightbox in place; documents go to the attachment page, where
    // the file previews inline next to its extracted Context tab.
    if (isImage) setLightbox(true);
    else window.location.assign(`/attachments/${id}`);
  }

  return (
    <>
      <CardShell>
        <CardBody>
          <button
            type="button"
            onClick={open}
            disabled={query.isPending || query.isError}
            className="flex w-full items-center gap-3 text-left disabled:opacity-60"
          >
            <CardBadge>
              {isImage ? (
                <ImageIcon aria-hidden className="size-[18px]" />
              ) : query.isError ? (
                <PaperclipIcon aria-hidden className="size-[18px]" />
              ) : (
                <FileTextIcon aria-hidden className="size-[18px]" />
              )}
            </CardBadge>
            <span className="min-w-0 flex-1">
              <CardEyebrow>Attachment</CardEyebrow>
              <span className="mt-0.5 block truncate font-bold text-portal-ink">{title}</span>
              <span className="mt-1 block truncate text-admin-micro text-admin-muted">
                {query.isError
                  ? 'Unavailable'
                  : (data?.description ?? (isImage ? 'Image — click to view' : 'Click to open'))}
              </span>
            </span>
            {isImage ? (
              <Maximize2Icon aria-hidden className="size-4 shrink-0 text-portal-muted" />
            ) : (
              <ExternalLinkIcon aria-hidden className="size-4 shrink-0 text-portal-muted" />
            )}
          </button>
        </CardBody>
      </CardShell>

      {isImage ? (
        <Dialog open={lightbox} onOpenChange={setLightbox}>
          <DialogContent className="w-[92vw] max-w-[1100px] overflow-hidden border-0 bg-black p-0 sm:max-w-[1100px]">
            <DialogTitle className="sr-only">{title}</DialogTitle>
            <img src={viewUrl} alt={title} className="mx-auto max-h-[85vh] w-auto object-contain" />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

/** A contact card: name, role, email and phone, with email/call/profile actions. */
function ContactCard({ id, label }: { id: string; label: React.ReactNode }): React.ReactElement {
  const query = useQuery({
    queryKey: ['assistant-contact', id],
    queryFn: ({ signal }) => getJson<ContactCardData>(`/users/${id}`, undefined, signal),
    staleTime: 60_000,
  });

  const c = query.data;
  // The model's label (the person's name) shows immediately; role and contact
  // actions fill in from the fetch.
  const name = c ? [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || null : null;
  const displayName = name ?? (typeof label === 'string' ? label : null);

  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--color-brand-sidebar)] text-[11px] font-bold text-white">
            {displayName ? initials(displayName) : <UserIcon aria-hidden className="size-4" />}
          </span>
          <span className="min-w-0 flex-1">
            <CardEyebrow>Contact</CardEyebrow>
            <span className="mt-0.5 block truncate font-bold text-portal-ink">
              {displayName ?? label}
            </span>
            {c?.role ? (
              <span className="mt-0.5 block truncate text-admin-micro text-admin-muted capitalize">
                {c.role}
              </span>
            ) : query.isPending ? (
              <Skeleton className="mt-1 h-2.5 w-20" />
            ) : null}
          </span>
        </span>

        <span className="mt-2.5 flex flex-wrap items-center gap-2">
          {query.isPending ? (
            <Skeleton className="h-6 w-44 rounded-md" />
          ) : (
            <>
              {c?.email ? (
                <a
                  href={`mailto:${c.email}`}
                  className="inline-flex items-center gap-1 rounded-md bg-brand/10 px-2.5 py-1 text-admin-micro font-bold text-brand no-underline hover:bg-brand/15"
                >
                  <MailIcon aria-hidden className="size-3" /> Email
                </a>
              ) : null}
              {c?.phone ? (
                <a
                  href={`tel:${c.phone}`}
                  className="inline-flex items-center gap-1 rounded-md border border-admin-line px-2.5 py-1 text-admin-micro font-bold text-portal-ink no-underline hover:bg-admin-subtle"
                >
                  <PhoneIcon aria-hidden className="size-3" /> Call
                </a>
              ) : null}
              <Link
                to={`/people/${id}`}
                className="inline-flex items-center rounded-md px-2.5 py-1 text-admin-micro font-bold text-brand no-underline hover:underline"
              >
                Open profile
              </Link>
            </>
          )}
        </span>
      </CardBody>
    </CardShell>
  );
}

/** Up to two initials from a display name, for the contact avatar. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

/**
 * An ad-hoc contact card for a person mentioned in the notes rather than held
 * as a portal user record.
 *
 * No fetch — the fields come inline from the assistant, which extracted them
 * from the notes. Surfaces the phone/email plainly (that is the whole point:
 * "do we have contact info?") and makes each one tap-to-call / tap-to-email.
 *
 * @param props - The contact's name and any role/phone/email found.
 * @returns The person card.
 */
function PersonCard({
  name,
  role,
  phone,
  email,
}: {
  name: string;
  role?: string;
  phone?: string;
  email?: string;
}): React.ReactElement {
  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--color-brand-sidebar)] text-[11px] font-bold text-white">
            {initials(name)}
          </span>
          <span className="min-w-0 flex-1">
            <CardEyebrow>Contact</CardEyebrow>
            <span className="mt-0.5 block truncate font-bold text-portal-ink">{name}</span>
            {role ? (
              <span className="mt-0.5 block truncate text-admin-micro text-admin-muted">{role}</span>
            ) : null}
          </span>
        </span>

        {phone || email ? (
          <span className="mt-2.5 flex flex-col gap-1.5">
            {phone ? (
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-1.5 text-admin-micro font-semibold text-brand no-underline hover:underline"
              >
                <PhoneIcon aria-hidden className="size-3 shrink-0" /> {phone}
              </a>
            ) : null}
            {email ? (
              <a
                href={`mailto:${email}`}
                className="inline-flex items-center gap-1.5 text-admin-micro font-semibold text-brand no-underline hover:underline"
              >
                <MailIcon aria-hidden className="size-3 shrink-0" /> {email}
              </a>
            ) : null}
          </span>
        ) : null}
      </CardBody>
    </CardShell>
  );
}

/**
 * A staff card: a portal user's full contact (name, title, phone, email) with a
 * link that opens their profile in a new tab. Used when the assistant lists
 * staff to choose between (e.g. picking a salesperson).
 *
 * @param props - The user id and their details (from the link's query string).
 * @returns The staff contact card.
 */
function StaffCard({
  id,
  name,
  title,
  phone,
  email,
}: {
  id: string;
  name: string;
  title?: string;
  phone?: string;
  email?: string;
}): React.ReactElement {
  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--color-brand-sidebar)] text-[11px] font-bold text-white">
            {initials(name)}
          </span>
          <span className="min-w-0 flex-1">
            <CardEyebrow>Staff</CardEyebrow>
            <span className="mt-0.5 block truncate font-bold text-portal-ink">{name}</span>
            {title ? (
              <span className="mt-0.5 block truncate text-admin-micro text-admin-muted">{title}</span>
            ) : null}
          </span>
        </span>

        {phone || email ? (
          <span className="mt-2.5 flex flex-col gap-1.5">
            {phone ? (
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-1.5 text-admin-micro font-semibold text-brand no-underline hover:underline"
              >
                <PhoneIcon aria-hidden className="size-3 shrink-0" /> {phone}
              </a>
            ) : null}
            {email ? (
              <a
                href={`mailto:${email}`}
                className="inline-flex items-center gap-1.5 text-admin-micro font-semibold text-brand no-underline hover:underline"
              >
                <MailIcon aria-hidden className="size-3 shrink-0" /> {email}
              </a>
            ) : null}
          </span>
        ) : null}

        <a
          href={`/people/${id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-admin-micro font-semibold text-brand no-underline hover:underline"
        >
          Open profile <ExternalLinkIcon aria-hidden className="size-3 shrink-0" />
        </a>
      </CardBody>
    </CardShell>
  );
}

/**
 * A program card: cover image, name, blurb and quick facts, with an "Open
 * program" link and — when the program has a video — a Play button that opens
 * it in a lightbox without leaving the chat.
 *
 * @param props - The program id and the model's label for instant title.
 * @returns The program card.
 */
function ProgramCard({ id, label }: { id: string; label: React.ReactNode }): React.ReactElement {
  const query = useQuery({
    queryKey: ['assistant-program', id],
    queryFn: ({ signal }) => getJson<ProgramCardData>(`/programs/${id}`, undefined, signal),
    staleTime: 300_000,
  });
  const [playing, setPlaying] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [startAt, setStartAt] = useState(0);
  const playerRef = useRef<InlinePlayerHandle | null>(null);

  const p = query.data;
  const image = p?.images?.[0];
  const meta = [p?.length, p?.groupSize].filter(Boolean).join(' · ');
  const hasVideo = typeof p?.video === 'string' && p.video.length > 0;

  /** Hands playback to the lightbox: pause inline, open at the same spot. */
  function expandVideo(): void {
    const handle = playerRef.current;
    setStartAt(handle ? handle.getTime() : 0);
    handle?.pause();
    setVideoOpen(true);
  }

  return (
    <CardShell>
      {playing && hasVideo ? (
        <span className="relative block aspect-video w-full bg-black">
          <InlineProgramVideo
            url={p.video as string}
            title={p?.name ?? 'Program video'}
            handleRef={playerRef}
          />
          <button
            type="button"
            aria-label="Expand video"
            onClick={expandVideo}
            className="absolute top-2 right-2 grid size-8 cursor-pointer place-items-center rounded-md bg-black/60 text-white hover:bg-black/80"
          >
            <Maximize2Icon aria-hidden className="size-4" />
          </button>
        </span>
      ) : image ? (
        <span className="relative block aspect-[16/9] w-full overflow-hidden bg-admin-subtle">
          <img src={image} alt="" className="h-full w-full object-cover" />
          {hasVideo ? (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label="Play program video"
              className="absolute inset-0 grid cursor-pointer place-items-center bg-black/25 transition-colors hover:bg-black/35"
            >
              <span className="grid size-12 place-items-center rounded-full bg-white/95 text-brand shadow-lg">
                <PlayIcon aria-hidden className="size-5 translate-x-0.5 fill-current" />
              </span>
            </button>
          ) : null}
        </span>
      ) : null}

      <CardBody>
        <CardEyebrow>Program</CardEyebrow>
        <span className="mt-0.5 block font-bold text-portal-ink">{p?.name ?? label}</span>

        {query.isPending ? (
          <Skeleton className="mt-1.5 h-3 w-48" />
        ) : p?.excerpt ? (
          <span className="mt-1 line-clamp-2 block text-admin-micro leading-relaxed text-admin-muted">
            {p.excerpt}
          </span>
        ) : null}
        {meta ? <span className="mt-1.5 block text-admin-micro text-admin-muted">{meta}</span> : null}

        <span className="mt-2.5 flex flex-wrap items-center gap-2">
          {hasVideo && !image && !playing ? (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-admin-micro font-bold text-white hover:brightness-110"
            >
              <PlayIcon aria-hidden className="size-3 fill-current" /> Play video
            </button>
          ) : null}
          <Link
            to={`/programs/${id}`}
            className="inline-flex items-center rounded-md border border-admin-line px-2.5 py-1 text-admin-micro font-bold text-portal-ink no-underline hover:bg-admin-subtle"
          >
            Open program
          </Link>
        </span>
      </CardBody>

      {hasVideo ? (
        <VideoLightbox
          open={videoOpen}
          onOpenChange={setVideoOpen}
          url={p.video as string}
          title={p?.name ?? 'Program video'}
          startAt={startAt}
        />
      ) : null}
    </CardShell>
  );
}

/** What kind of video a URL is, and its embed base when hosted. */
function videoSource(
  url: string,
): { kind: 'youtube' | 'vimeo'; embed: string } | { kind: 'file' } {
  const yt = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/i.exec(url);
  if (yt?.[1]) return { kind: 'youtube', embed: `https://www.youtube.com/embed/${yt[1]}` };
  const vimeo = /vimeo\.com\/(?:video\/)?(\d+)/i.exec(url);
  if (vimeo?.[1]) return { kind: 'vimeo', embed: `https://player.vimeo.com/video/${vimeo[1]}` };
  return { kind: 'file' };
}

/** What the inline player exposes to its card: where it is, and pause. */
interface InlinePlayerHandle {
  getTime: () => number;
  pause: () => void;
}

/**
 * The in-chat video player, with position tracking so Expand can hand off to
 * the lightbox at the same spot.
 *
 * Position comes from the element for direct files, and from each host's
 * postMessage protocol for YouTube (enablejsapi + infoDelivery) and Vimeo
 * (timeupdate events) — best-effort: if a host never reports, expanding simply
 * starts the lightbox from the beginning.
 *
 * @param props - The video URL, a title, and a ref to receive the handle.
 * @returns The inline player.
 */
function InlineProgramVideo({
  url,
  title,
  handleRef,
}: {
  url: string;
  title: string;
  handleRef: React.MutableRefObject<InlinePlayerHandle | null>;
}): React.ReactElement {
  const source = videoSource(url);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const timeRef = useRef(0);

  // Host-reported playback position + pause plumbing.
  useEffect(() => {
    function onMessage(event: MessageEvent): void {
      if (source.kind === 'youtube' && event.origin === 'https://www.youtube.com') {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          const time = data?.info?.currentTime;
          if (typeof time === 'number') timeRef.current = time;
        } catch {
          /* not ours */
        }
      }
      if (source.kind === 'vimeo' && event.origin === 'https://player.vimeo.com') {
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (data?.event === 'ready') {
            frameRef.current?.contentWindow?.postMessage(
              JSON.stringify({ method: 'addEventListener', value: 'timeupdate' }),
              'https://player.vimeo.com',
            );
          }
          const seconds = data?.data?.seconds;
          if (typeof seconds === 'number') timeRef.current = seconds;
        } catch {
          /* not ours */
        }
      }
    }
    window.addEventListener('message', onMessage);

    // YouTube only reports after a listening handshake.
    const handshake =
      source.kind === 'youtube'
        ? window.setInterval(() => {
            frameRef.current?.contentWindow?.postMessage(
              JSON.stringify({ event: 'listening', id: 'bce-inline', channel: 'widget' }),
              'https://www.youtube.com',
            );
          }, 500)
        : null;

    handleRef.current = {
      getTime: () => (videoRef.current ? videoRef.current.currentTime : timeRef.current),
      pause: () => {
        if (videoRef.current) {
          videoRef.current.pause();
          return;
        }
        if (source.kind === 'youtube') {
          frameRef.current?.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
            'https://www.youtube.com',
          );
        }
        if (source.kind === 'vimeo') {
          frameRef.current?.contentWindow?.postMessage(
            JSON.stringify({ method: 'pause' }),
            'https://player.vimeo.com',
          );
        }
      },
    };

    return () => {
      window.removeEventListener('message', onMessage);
      if (handshake !== null) window.clearInterval(handshake);
      handleRef.current = null;
    };
  }, [source.kind, handleRef]);

  if (source.kind === 'file') {
    // eslint-disable-next-line jsx-a11y/media-has-caption
    return <video ref={videoRef} src={url} controls autoPlay className="h-full w-full" />;
  }

  const src =
    source.kind === 'youtube'
      ? `${source.embed}?autoplay=1&enablejsapi=1`
      : `${source.embed}?autoplay=1`;

  return (
    <iframe
      ref={frameRef}
      src={src}
      title={title}
      className="h-full w-full"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );
}

/** A modal video player, opened from a program card's Expand control. */
function VideoLightbox({
  open,
  onOpenChange,
  url,
  title,
  startAt = 0,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  title: string;
  /** Seconds into the video to resume from (handed off from inline playback). */
  startAt?: number;
}): React.ReactElement {
  const source = videoSource(url);
  const seconds = Math.max(0, Math.floor(startAt));

  let embedSrc: string | null = null;
  if (source.kind === 'youtube') {
    embedSrc = `${source.embed}?autoplay=1${seconds > 0 ? `&start=${seconds}` : ''}`;
  } else if (source.kind === 'vimeo') {
    embedSrc = `${source.embed}?autoplay=1${seconds > 0 ? `#t=${seconds}s` : ''}`;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[92vw] max-w-[1200px] overflow-hidden border-0 bg-black p-0 sm:max-w-[1200px]">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="aspect-video w-full bg-black">
          {open ? (
            embedSrc ? (
              <iframe
                src={embedSrc}
                title={title}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video
                src={url}
                controls
                autoPlay
                className="h-full w-full"
                onLoadedMetadata={(event) => {
                  if (seconds > 0) event.currentTarget.currentTime = seconds;
                }}
              />
            )
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A note the assistant drafted from the conversation, pending the staff
 * member's approval.
 *
 * The write happens HERE, on Save, through the same authenticated notes API the
 * composer uses — the model only proposes. Dismiss simply collapses the card.
 *
 * @param props - The target event and the drafted category/subject/body.
 * @returns The draft-note card.
 */
function DraftNoteCard({
  eventId,
  category,
  subject,
  body,
}: {
  eventId: string;
  category: string;
  subject: string;
  body: string;
}): React.ReactElement {
  const [dismissed, setDismissed] = useState(false);
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: () =>
      createEventNote(eventId, {
        category: (NOTE_CATEGORIES as readonly string[]).includes(category)
          ? (category as NoteCategory)
          : 'Basic',
        subject: subject.trim() || 'Note',
        body,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event-notes', eventId] }),
  });

  if (dismissed) {
    return (
      <span className="my-1 block text-admin-micro text-admin-muted italic">Note draft dismissed.</span>
    );
  }

  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <CardBadge>
            <StickyNoteIcon aria-hidden className="size-[18px]" />
          </CardBadge>
          <span className="min-w-0 flex-1">
            <CardEyebrow>{save.isSuccess ? 'Note saved' : 'Draft note'}</CardEyebrow>
            <span className="mt-0.5 block font-bold text-portal-ink">{subject || 'Note'}</span>
            <span className="mt-0.5 block text-admin-micro text-admin-muted">{category}</span>
          </span>
          {save.isSuccess ? (
            <CheckIcon aria-hidden className="size-4 shrink-0 text-emerald-600" />
          ) : null}
        </span>

        <span className="mt-2 block rounded-lg bg-admin-bg px-2.5 py-2">
          <NoteBody body={body} className="note-prose text-admin-micro" />
        </span>

        {save.isError ? (
          <span className="mt-2 block text-admin-micro font-semibold text-red-600">
            Could not save the note. Try again.
          </span>
        ) : null}

        {save.isSuccess ? (
          <span className="mt-2.5 block">
            <Link
              to={`/events/${eventId}?tab=notes`}
              className="text-admin-micro font-bold text-brand no-underline hover:underline"
            >
              Open event notes
            </Link>
          </span>
        ) : (
          <span className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-admin-micro font-bold text-white hover:brightness-110 disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Save note'}
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              disabled={save.isPending}
              className="inline-flex cursor-pointer items-center rounded-md px-2 py-1 text-admin-micro font-bold text-admin-muted hover:text-portal-ink"
            >
              Dismiss
            </button>
          </span>
        )}
      </CardBody>
    </CardShell>
  );
}

/** Draft email payload from `GET /assistant/email/drafts/:id`. */
interface EmailDraftData {
  readonly id: string;
  readonly to: { readonly name: string; readonly email: string; readonly role: string };
  readonly subject: string;
  readonly status: 'draft' | 'sent';
  readonly html: string;
}

/**
 * An email BESTie drafted, pending the staff member's explicit approval.
 *
 * The model can only create drafts; THIS button is the only path to sending.
 * The rendered preview comes from the server (same renderer that sends), so
 * what is shown is exactly what goes out. Recipient policy is enforced
 * server-side — drafts to client users can neither be created nor sent.
 *
 * @param props - The server-side draft id.
 * @returns The email preview/approval card.
 */
export function DraftEmailCard({ draftId }: { draftId: string }): React.ReactElement {
  const [dismissed, setDismissed] = useState(false);
  const query = useQuery({
    queryKey: ['assistant-email-draft', draftId],
    queryFn: ({ signal }) =>
      getJson<EmailDraftData>(`/assistant/email/drafts/${draftId}`, undefined, signal),
  });
  const send = useMutation({
    mutationFn: () => sendJson<{ sent: boolean }>('post', `/assistant/email/drafts/${draftId}/send`),
  });

  if (dismissed) {
    return (
      <span className="my-1 block text-admin-micro text-admin-muted italic">
        Email draft dismissed — nothing was sent.
      </span>
    );
  }
  if (query.isPending) {
    return <span className="my-1 block text-admin-micro text-admin-muted">Loading email draft…</span>;
  }
  if (query.isError || !query.data) {
    return (
      <span className="my-1 block text-admin-micro text-admin-muted italic">
        This email draft is no longer available.
      </span>
    );
  }

  const draft = query.data;
  const alreadySent = draft.status === 'sent' || send.isSuccess;

  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <CardBadge>
            <MailIcon aria-hidden className="size-[18px]" />
          </CardBadge>
          <span className="min-w-0 flex-1">
            <CardEyebrow>{alreadySent ? 'Email sent' : 'Email draft — needs your approval'}</CardEyebrow>
            <span className="mt-0.5 block font-bold text-portal-ink">{draft.subject}</span>
            <span className="mt-0.5 block text-admin-micro text-admin-muted">
              To {draft.to.name} ({draft.to.email}) · {draft.to.role}
            </span>
          </span>
          {alreadySent ? <CheckIcon aria-hidden className="size-4 shrink-0 text-emerald-600" /> : null}
        </span>

        <span className="mt-2 block overflow-hidden rounded-lg border border-admin-line">
          <iframe
            title={`Email preview: ${draft.subject}`}
            srcDoc={draft.html}
            sandbox=""
            className="h-[340px] w-full bg-white"
          />
        </span>

        {send.isError ? (
          <span className="mt-2 block text-admin-micro font-semibold text-red-600">
            {errorMessage(send.error)}
          </span>
        ) : null}

        {alreadySent ? null : (
          <span className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={() => send.mutate()}
              disabled={send.isPending}
              className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-admin-micro font-bold text-white hover:brightness-110 disabled:opacity-50"
            >
              {send.isPending ? 'Sending…' : `Approve & send to ${draft.to.name}`}
            </button>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              disabled={send.isPending}
              className="inline-flex cursor-pointer items-center rounded-md px-2 py-1 text-admin-micro font-bold text-admin-muted hover:text-portal-ink"
            >
              Dismiss
            </button>
          </span>
        )}
      </CardBody>
    </CardShell>
  );
}

/** Event card payload from `GET /events/:id` (the fields the card shows). */
interface EventCardData {
  readonly programDisplayName?: string;
  readonly programName?: string;
  readonly contractNumber?: string;
  readonly stageName?: string;
  readonly eventDate?: string;
  readonly timeStart?: string;
  readonly timeEnd?: string;
  readonly timeStartString?: string;
  readonly timeEndString?: string;
  readonly timeZone?: string;
  readonly longTimeZone?: string;
  readonly participants?: number;
  readonly account?: { readonly name?: string };
  readonly startVenue?: {
    readonly name?: string;
    readonly city?: string;
    readonly state?: string;
    readonly timezone?: { readonly id?: string; readonly name?: string };
  };
}

/**
 * A rich event card: program, client, date/time, venue, size and stage, with
 * an Open-event action. Shows the model's label instantly, then swaps in the
 * authoritative record.
 *
 * @param props - The event id and the model's label for instant title.
 * @returns The event card.
 */
function EventCard({ id, label }: { id: string; label: React.ReactNode }): React.ReactElement {
  const query = useQuery({
    queryKey: ['assistant-event', id],
    queryFn: ({ signal }) => getJson<EventCardData>(`/events/${id}`, undefined, signal),
    staleTime: 60_000,
  });

  const e = query.data;
  const title = e ? (e.programDisplayName ?? e.programName ?? 'Event') : null;
  const date = formatEventDate(e, 'medium') ?? null;
  // Carries the venue's zone: an assistant answer read on a phone in another
  // state must not imply the event runs on the reader's clock.
  const time = formatEventTime(e, '–') ?? null;
  const where =
    [e?.startVenue?.name, [e?.startVenue?.city, e?.startVenue?.state].filter(Boolean).join(', ')]
      .filter(Boolean)
      .join(' · ') || null;

  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <CardBadge>
            <CalendarDaysIcon aria-hidden className="size-[18px]" />
          </CardBadge>

          <span className="min-w-0 flex-1">
            <CardEyebrow>{e?.account?.name ?? 'Event'}</CardEyebrow>
            <span className="mt-0.5 block font-bold text-portal-ink">{title ?? label}</span>
            {query.isPending ? (
              <Skeleton className="mt-1.5 h-3 w-48" />
            ) : query.isError || !e ? (
              <span className="mt-1 block text-admin-micro text-admin-muted">
                Details unavailable
              </span>
            ) : (
              <>
                {date || time ? (
                  <span className="mt-1 block text-admin-micro text-admin-muted">
                    {[date, time].filter(Boolean).join(' · ')}
                  </span>
                ) : null}
                {where || e.participants ? (
                  <span className="mt-0.5 block text-admin-micro text-admin-muted">
                    {[where, e.participants ? `${e.participants} participants` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                ) : null}
              </>
            )}
          </span>

          {e?.stageName ? (
            <span
              className={cn(
                'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold',
                STAGE_TONE_CLASS[eventStageTone(e.stageName)],
              )}
            >
              {e.stageName}
            </span>
          ) : query.isPending ? (
            <Skeleton className="h-4 w-14 shrink-0 rounded-full" />
          ) : null}
        </span>

        <span className="mt-2.5 flex flex-wrap items-center gap-2">
          <Link
            to={`/events/${id}`}
            className="inline-flex items-center rounded-md bg-brand px-2.5 py-1 text-admin-micro font-bold text-white no-underline hover:brightness-110"
          >
            Open event
          </Link>
          {e?.contractNumber ? (
            <span className="font-mono text-admin-micro text-admin-muted">{e.contractNumber}</span>
          ) : null}
        </span>
      </CardBody>
    </CardShell>
  );
}

/**
 * A proposal, fetched by id so the figures are the stored ones. The assistant
 * never edits or sends it — the card just opens it.
 *
 * @param props - The proposal id and the link text.
 * @returns The proposal card.
 */
function ProposalCard({ id, label }: { id: string; label: React.ReactNode }): React.ReactElement {
  const query = useQuery({
    queryKey: ['assistant-proposal', id],
    queryFn: () => getProposal(id),
    staleTime: 30_000,
  });

  const p = query.data;
  const priced = (p?.pricing.options.some((o) => o.programs.length > 0)) ?? false;
  const netText =
    p && priced
      ? p.totals.net !== null
        ? `${formatMoney(p.totals.net)} net`
        : `${p.totals.options.length} options`
      : 'Not priced';

  return (
    <CardShell>
      <CardBody>
        <span className="flex items-start gap-3">
          <CardBadge>
            <FileTextIcon aria-hidden className="size-[18px]" />
          </CardBadge>

          <span className="min-w-0 flex-1">
            <CardEyebrow>{p?.opportunity?.accountName ?? 'Proposal'}</CardEyebrow>
            <span className="mt-0.5 block font-bold text-portal-ink">{p?.name ?? label}</span>
            {query.isPending ? (
              <Skeleton className="mt-1.5 h-3 w-48" />
            ) : query.isError || !p ? (
              <span className="mt-1 block text-admin-micro text-admin-muted">Details unavailable</span>
            ) : (
              <span className="mt-1 block text-admin-micro text-admin-muted">
                {[netText, p.totals.participants > 0 ? `${p.totals.participants} participants` : null, p.opportunity?.stageName ?? null]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          </span>
        </span>

        <span className="mt-2.5 flex flex-wrap items-center gap-2">
          <Link
            to={`/proposals/${id}`}
            className="inline-flex items-center rounded-md bg-brand px-2.5 py-1 text-admin-micro font-bold text-white no-underline hover:brightness-110"
          >
            Open proposal
          </Link>
        </span>
      </CardBody>
    </CardShell>
  );
}
